/**
 * NET — the client half of DUCS multiplayer.
 *
 * Design in one paragraph: the server is authoritative over position, health,
 * damage, money and rounds. The client runs the *same* movement code locally so
 * its own avatar responds with zero latency, samples its input once per fixed
 * step and ships those commands upstream, then gently corrects its avatar when
 * the server disagrees. Remote players are interpolated two snapshots in the
 * past. Shooting is cosmetic locally and validated upstream — the server sends
 * a hit confirmation, which is the only thing that draws a hitmarker.
 *
 * Timing
 *   fixedUpdate  builds one command per 120 Hz step and pushes it to the wire
 *   update       sends the batched commands (~60 Hz), drives remote visuals
 *   onmessage    applies snapshots into `this.state`, which the HUD polls
 */
import {
  MSG, EV, BUTTON, TEAM, TEAM_NAME, PROTOCOL_VERSION,
  makeCmd, packCmd, weaponById, STANCE,
} from './protocol.js';
import { createMatchState } from '../game/state.js';
import { RemotePlayers } from '../game/remote.js';

/** Server ticks of interpolation delay the client renders behind. 66 ms. */
const INTERP_TICKS = 8;
/** Corrections smaller than this are ignored — chasing them causes jitter. */
const CORRECTION_EPSILON = 0.04;
/** Corrections larger than this are teleports (respawn, big desync): snap. */
const CORRECTION_SNAP = 2.0;
/** How fast a soft correction converges, per second. */
const CORRECTION_RATE = 12;

export class NetSystem {
  static id = 'net';
  static deps = ['player', 'physics', 'world'];

  constructor() {
    this.state = createMatchState();
    this.ws = null;
    this.connected = false;
    this.remotes = null;
    this.pending = [];        // commands awaiting send
    this.lastSendAt = 0;
    this.cmdTick = 0;

    /* Clock estimation for lag compensation. */
    this.serverTickAtSnap = 0;
    this.snapLocalTime = 0;
    this.estimatedServerTick = 0;
    this.latencyMs = 0;

    /* Local prediction correction. */
    this._correction = { x: 0, y: 0, z: 0, active: false };
    this._pendingFire = false;

    /* Preallocated scratch — the fixed step runs 120 times a second. */
    this._cmd = makeCmd();
    this._move = { x: 0, y: 0 };
    this._pingTimer = 0;
    this._pingSentAt = 0;
  }

  /* ─────────────────────────── lifecycle ─────────────────────────── */

  async init(ctx) {
    this.ctx = ctx;
    this.player = ctx.peek('player');
    this.config = ctx.config;

    this.remotes = new RemotePlayers(ctx);
    this.remotes.init();

    // Where to connect. `?server=` overrides; otherwise match the page origin:
    // https pages connect to same-origin wss (tunnels/proxies/reverse-proxy
    // friendly), plain http assumes the dev layout (vite on :5173, game
    // server on :8080).
    const params = new URLSearchParams(location.search);
    const override = params.get('server');
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = override ?? (location.protocol === 'https:' ? location.host : `${location.hostname}:8080`);
    this.url = override?.includes('://') ? override : `${proto}//${host}`;

    this.state.connecting = true;
    this.connect();

    // NOTE: the offline garrison is not disabled here. It is disabled on
    // WELCOME, once a server has actually answered — otherwise a client started
    // before the server (or with the server down) would be left on an empty
    // map with nothing to shoot at.
    return this;
  }

  connect() {
    if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) return;
    let ws;
    try {
      ws = new WebSocket(this.url);
    } catch (err) {
      console.warn('[net] could not open socket', err?.message);
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.state.connecting = false;
      ws.send(JSON.stringify({ t: MSG.HELLO, name: this.playerName(), protocol: PROTOCOL_VERSION }));
    };

    ws.onmessage = (e) => this.onMessage(JSON.parse(e.data));

    ws.onclose = () => {
      this.connected = false;
      this.state.connected = false;
      console.warn('[net] disconnected');
      this._scheduleReconnect();
    };

    ws.onerror = () => { /* onclose always follows; nothing to do here */ };
  }

  _scheduleReconnect() {
    if (this._reconnectTimer) return;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      console.info('[net] reconnecting…');
      this.connect();
    }, 2500);
  }

  playerName() {
    const params = new URLSearchParams(location.search);
    return params.get('name') ?? `player${Math.floor(Math.random() * 900 + 100)}`;
  }

  /* ─────────────────────────── inbound ─────────────────────────── */

  onMessage(m) {
    switch (m.t) {
      case MSG.WELCOME: return this.onWelcome(m);
      case MSG.SNAPSHOT: return this.onSnapshot(m);
      case MSG.PONG:
        this.latencyMs = Math.round(performance.now() - this._pingSentAt);
        this.state.latencyMs = this.latencyMs;
        return;
      case MSG.REJECT:
        console.error('[net] server rejected the connection:', m.reason);
        this.ws?.close();
        return;
      default:
        if (m.t === MSG.EVENT) this.onEvent(m);
        return;
    }
  }

  onWelcome(m) {
    this.connected = true;
    this.state.connected = true;
    // Fresh connection: prime the ping clock so the first latency reading is
    // real, not performance.now() minus a boot-time zero.
    this._lastPingAt = 0;
    this._pingSentAt = performance.now();
    this.state.id = m.id;
    this.state.name = m.name;
    this.state.team = m.team;
    this.state.money = m.money;
    this.state.map = m.map;
    this.state.mode = m.match?.mode ?? this.state.mode;
    this.cmdTick = m.tick | 0;
    this.serverTickAtSnap = m.tick | 0;
    this.snapLocalTime = performance.now();
    // Set before the first snapshot arrives: until this is known, remote
    // rendering cannot tell the local player apart from the opposition and
    // would happily spawn a ghost of you standing on your own shoulder.
    if (this.remotes) this.remotes.localId = m.id;
    console.info(`[net] joined ${m.map} as #${m.id} (${TEAM_NAME[m.team]}) — mode ${this.state.mode}`);

    // Now that a server is authoritative, retire the offline garrison: its
    // soldiers would otherwise be indistinguishable from real opponents and
    // would shoot at you during a match.
    const ai = this.ctx.peek('ai');
    if (ai && !ai._ducsRetired) {
      ai._ducsRetired = true;
      ai.enabled = false;
      ai.root.visible = false;
      console.info('[net] offline AI garrison retired — the server owns the opposition');
    }
  }

  onSnapshot(m) {
    const now = performance.now();
    const s = this.state;

    // Clock estimate: the server's tick advances at 120 Hz from the last
    // snapshot we saw. This is what we tell the server to rewind to.
    const dt = (now - this.snapLocalTime) / 1000;
    this.serverTickAtSnap = m.tick;
    this.snapLocalTime = now;
    this.estimatedServerTick = m.tick + dt * 120;
    s.serverTick = m.tick;

    // ---- match state ----
    if (m.match) {
      const mm = m.match;
      s.mode = mm.mode ?? s.mode;
      s.score = mm.score ?? s.score;
      s.over = !!mm.over;
      s.winner = mm.winner ?? 0;
      if (mm.round !== undefined) s.round = mm.round;
      if (mm.phase) s.phase = mm.phase;
      if (mm.timeLeft !== undefined) s.timeLeft = mm.timeLeft;
      s.inBuyWindow = s.phase === 'live' && mm.timeLeft !== undefined && s.mode === 'snd'
        ? mm.timeLeft > 115 - 15 : false;
      if (mm.bomb) {
        s.bomb.state = mm.bomb.state;
        s.bomb.site = mm.bomb.site;
        s.bomb.x = mm.bomb.x; s.bomb.y = mm.bomb.y; s.bomb.z = mm.bomb.z;
        s.bomb.fuse = mm.bomb.fuse ?? 0;
        s.bomb.plantProgress = mm.bomb.plantProgress ?? 0;
        s.bomb.defuseProgress = mm.bomb.defuseProgress ?? 0;
        s.bomb.carrier = mm.bomb.carrier;
        s.hasBomb = mm.bomb.carrier === s.id;
      }
    }

    // ---- players ----
    const ids = new Set();
    let mine = null;
    for (const rec of m.players ?? []) {
      ids.add(rec.i);
      if (rec.i === s.id) mine = rec;
      else this.remotes.feed(rec, now / 1000);
    }
    this.remotes.prune(ids);

    if (mine) {
      this.applyLocal(mine);
      s.hp = mine.hp;
      s.alive = mine.al === 1;
      s.armor = mine.ap ?? 0;
      s.weapon = mine.wp ?? 0;
      s.ammo = mine.am ?? 0;
      s.reloading = mine.rs === 1;
      s.ads = (mine.ad ?? 0) / 255;
      const w = weaponById(s.weapon);
      s.weaponKey = w.key;
      s.weaponName = w.name;
      s.magSize = w.mag;
    }

    // ---- events ----
    if (m.events) for (const e of m.events) this.onEvent(e);
  }

  /**
   * Reconcile the locally predicted avatar with the server's authority.
   *
   * Both sides run the identical Movement class over the identical level, so
   * disagreement means something the client could not know about: it was
   * blocked, it was moved by a round restart, or it drifted. Small errors are
   * blended away; anything large is a teleport.
   */
  applyLocal(rec) {
    const p = this.player;
    if (!p?.movement?.character) return;
    const m = p.movement;
    const c = m.character;

    const dx = rec.x - c.position.x;
    const dy = rec.y - c.position.y;
    const dz = rec.z - c.position.z;
    const d = Math.hypot(dx, dy, dz);

    this._correction.active = false;
    if (d < CORRECTION_EPSILON) return;

    if (d > CORRECTION_SNAP) {
      m.teleport(rec.x, rec.y, rec.z);
      m.yaw = rec.w;
      m.pitch = rec.p;
      this.state.correction = d;
      return;
    }

    this._correction.x = dx;
    this._correction.y = dy;
    this._correction.z = dz;
    this._correction.active = true;
    this.state.correction = d;
  }

  onEvent(e) {
    const s = this.state;
    const now = performance.now();

    switch (e.kind) {
      case EV.HIT_CONFIRM:
        s.hitmarker.t = now / 1000;
        s.hitmarker.headshot = !!e.headshot;
        s.hitmarker.killed = !!e.killed;
        break;

      case EV.DAMAGE:
        if (e.target === s.id) {
          s.damageFlash.t = now / 1000;
          s.damageFlash.amount = e.amount;
          const r = this.remotes?.actors.get(e.from);
          if (r) { s.damageFlash.fromX = r.rx; s.damageFlash.fromZ = r.rz; }
          // Drive the local health system too, so the low-health vignette, the
          // heartbeat and the directional damage indicator all still work.
          // The server's number is what counts; this is presentation.
          const p = this.player;
          if (p && !p.health.dead) {
            p.health.damage(e.amount, r ? { x: r.rx, y: r.ry, z: r.rz } : null,
              { yaw: p.movement?.yaw ?? 0 });
          }
        } else {
          this.remotes?.actors.get(e.target)?.playHit(e.part ?? 'torso', 1, Math.min(1.4, e.amount / 45));
        }
        break;

      case EV.KILL:
        s.killfeed.push({
          killer: e.killer, victim: e.victim, weapon: e.weapon,
          headshot: e.headshot, killerTeam: e.killerTeam, victimTeam: e.victimTeam,
          t: now / 1000,
        });
        if (s.killfeed.length > 6) s.killfeed.shift();
        if (e.victim === s.id) { s.deaths++; s.alive = false; }
        if (e.killer === s.id) {
          s.kills++;
          // Reward is granted by the server; we reflect the local counter only.
        }
        break;

      case EV.FIRE: {
        if (e.who === s.id) break;
        this.remotes?.actors.get(e.who)?.playFire(0.8);
        break;
      }

      case EV.RELOAD:
        if (e.who !== s.id) this.remotes?.actors.get(e.who)?.animator?.reload(2.4);
        break;

      case EV.ROUND_START:
        s.round = e.round;
        s.phase = 'live';
        s.hasBomb = e.bombCarrier === s.id;
        s.bomb.state = 'carried';
        s.bomb.carrier = e.bombCarrier;
        break;

      case EV.ROUND_END:
        s.phase = 'roundOver';
        if (e.score) s.score = e.score;
        if (e.money && s.id != null && e.money[s.id] != null) s.money = e.money[s.id];
        break;

      case EV.BOMB_PLANTED:
        s.bomb.state = 'planted';
        s.bomb.site = e.site;
        s.bomb.x = e.x; s.bomb.y = e.y; s.bomb.z = e.z;
        break;

      case EV.BOMB_DEFUSED:
      case EV.BOMB_DROPPED:
        s.bomb.state = e.kind === EV.BOMB_DEFUSED ? 'defused' : 'dropped';
        if (e.kind === EV.BOMB_DROPPED) { s.bomb.x = e.x; s.bomb.y = e.y; s.bomb.z = e.z; }
        s.bomb.carrier = null;
        s.hasBomb = false;
        break;

      case EV.MATCH_END:
        s.matchOver = true;
        s.winner = e.winner;
        if (e.score) s.score = e.score;
        break;

      case EV.MONEY:
        if (e.id === s.id) s.money = e.money;
        break;

      case EV.BUY_OK:
        if (e.money != null) s.money = e.money;
        break;

      case EV.TEAM_SWAP:
        s.team = s.team === TEAM.T ? TEAM.CT : TEAM.T;
        break;

      default:
        break;
    }
  }

  /* ─────────────────────────── outbound ─────────────────────────── */

  /** One fixed step: build the command for this tick and queue it. */
  fixedUpdate(h, ctx) {
    if (!this.connected) return;
    const p = this.player;
    if (!p?.movement?.character) return;

    const input = ctx.input;
    const m = p.movement;

    // Movement axes. Input is read directly rather than through movement.cmd
    // because the server needs the *raw* axes, not the stance-scaled wish dir.
    input.moveVector(this._move);

    const c = this._cmd;
    c.tick = this.cmdTick++;
    c.mx = this._move.x;
    c.my = this._move.y;
    c.yaw = m.yaw;
    c.pitch = m.pitch;
    c.aim = this.aimTick();

    let b = 0;
    if (input.action('jump')) b |= BUTTON.JUMP;
    if (input.action('crouch')) b |= BUTTON.CROUCH;
    if (input.action('prone')) b |= BUTTON.PRONE;
    if (input.action('sprint')) b |= BUTTON.SPRINT;
    if (input.action('leanLeft')) b |= BUTTON.LEAN_L;
    if (input.action('leanRight')) b |= BUTTON.LEAN_R;
    if (input.ads) b |= BUTTON.ADS;
    if (input.action('reload')) b |= BUTTON.RELOAD;
    if (input.action('use')) b |= BUTTON.USE;
    if (input.action('melee')) b |= BUTTON.MELEE;
    if (input.action('grenade')) b |= BUTTON.SWAP;
    // Fire is read from the weapon system so burst/single/auto semantics match
    // what the viewmodel is actually doing.
    const weapons = ctx.peek('weapons');
    if (weapons?.firing) b |= BUTTON.FIRE;
    c.buttons = b;

    this.pending.push(packCmd(c));
  }

  /**
   * The server tick this client is currently *rendering*. Sent with every shot
   * so lag compensation rewinds to exactly what the shooter saw.
   */
  aimTick() {
    const now = performance.now();
    const elapsed = (now - this.snapLocalTime) / 1000;
    const tick = Math.round(this.serverTickAtSnap + elapsed * 120 - INTERP_TICKS);
    return tick < 0 ? 0 : tick;
  }

  /* ─────────────────────────── frame ─────────────────────────── */

  update(dt, ctx) {
    const now = performance.now();

    // Send batched input at ~60 Hz.
    if (this.connected && this.pending.length && now - this.lastSendAt >= 15) {
      this.lastSendAt = now;
      this.send({ t: MSG.INPUT, cmds: this.pending });
      this.pending.length = 0;
    }

    // Latency probe once a second — wall-clock, not accumulated dt: at low
    // frame rates (software rendering, hidden tab) a dt-driven timer crawls
    // and latency stays at 0 forever.
    if (this.connected && now - (this._lastPingAt ?? 0) >= 1000) {
      this._lastPingAt = now;
      this._pingSentAt = now;
      this.send({ t: MSG.PING, at: now });
    }

    // Soft correction: pull the predicted pose toward the authoritative one.
    if (this._correction.active) {
      const p = this.player;
      const m = p?.movement;
      if (m?.character) {
        const k = Math.min(1, CORRECTION_RATE * dt);
        const c = m.character;
        c.teleport(
          c.position.x + this._correction.x * k,
          c.position.y + this._correction.y * k,
          c.position.z + this._correction.z * k
        );
        this._correction.x -= this._correction.x * k;
        this._correction.y -= this._correction.y * k;
        this._correction.z -= this._correction.z * k;
        const d = Math.hypot(this._correction.x, this._correction.y, this._correction.z);
        if (d < CORRECTION_EPSILON) this._correction.active = false;
      }
    }

    // Remote players.
    if (this.remotes) this.remotes.update(dt, now / 1000, this.state.id);

    this.state.clientTick = this.cmdTick;
    this.state.estimatedServerTick = Math.round(this.estimatedServerTick);
  }

  /* ─────────────────────────── commands ─────────────────────────── */

  send(obj) {
    if (!this.ws || this.ws.readyState !== 1) return;
    try { this.ws.send(JSON.stringify(obj)); } catch { /* socket going away */ }
  }

  buy(key) { this.send({ t: MSG.BUY, item: key }); }

  requestTeam(team) { this.send({ t: MSG.TEAM, team }); }

  chat(text) { this.send({ t: MSG.CHAT, text }); }

  dispose() {
    if (this._reconnectTimer) clearTimeout(this._reconnectTimer);
    try { this.ws?.close(); } catch { /* already closed */ }
    this.remotes?.dispose();
  }
}
